import { useContext, useState } from 'react';
import { When } from 'react-if';
import { Button } from '../../../../../common/view/button/button/button';
import { Color } from '../../../../../common/view/color/color';
import { EffectContext } from '../../../../../common/view/effect/effect.context';
import { ModalStyle } from '../../../../../common/view/effect/modal/modal.style';
import { MonoIcon } from '../../../../../common/view/icon/mono/mono-icon';
import { ImmerFuncs } from '../../../../../external/immer/immer.funcs';
import { ServerApi } from '../../../../../external/server/server.api';
import { StringFuncs } from '../../../../../foundation/js/string.funcs';
import { GapY } from '../../../../../foundation/layout/gap-y';
import { StackX } from '../../../../../foundation/layout/stack-x';
import { TripleX } from '../../../../../foundation/layout/triple-x';
import { EffectWire } from '../../../../core/effect/effect.wire';
import { SnoozeEffectDataWire } from '../../../../core/effect/effects/snooze/snooze-effect-data.wire';

export function SnoozeEffectEditModal({
  effect,
  onOk,
}: {
  effect: EffectWire<SnoozeEffectDataWire>;
  onOk: (effect: EffectWire<SnoozeEffectDataWire>) => void;
}) {
  const { modalApi } = useContext(EffectContext);
  const [text, setText] = useState(effect.data.text);
  const [date, setDate] = useState(effect.data.date);
  const [loading, setLoading] = useState(false);

  const stage = StringFuncs.isEmpty(date) ? 1 : 2;

  const handleTextToDate = async () => {
    setLoading(true);
    const result = await ServerApi.textToDate({ text, referenceDate: Date.now(), startingWeek: 'mon' });
    setDate(result.date);
    setLoading(false);
  };

  return (
    <ModalStyle.Container style={{ width: '400px' }}>
      <ModalStyle.Header>
        <TripleX.Container>
          <TripleX.Rest>
            <ModalStyle.Title>{'Set a date'}</ModalStyle.Title>
          </TripleX.Rest>
          <TripleX.Side>
            <Button
              icon={MonoIcon.close}
              size={18}
              color={Color.white7}
              onClick={() => {
                modalApi.pop();
              }}
            />
          </TripleX.Side>
        </TripleX.Container>
      </ModalStyle.Header>
      <ModalStyle.Content>
        <ModalStyle.ContentTitle>Describe the date you want to snooze until</ModalStyle.ContentTitle>
        <GapY $gap={'2px'} />
        <ModalStyle.Text2>Eg: Tomorrow, Sunday next week, April 1st</ModalStyle.Text2>
        <GapY $gap={'8px'} />
        <ModalStyle.Input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
        ></ModalStyle.Input>
        <GapY $gap={'2px'} />
        <ModalStyle.Text2>Hour and minute not supported yet</ModalStyle.Text2>

        <When condition={stage === 2}>
          <GapY $gap={'20px'} />
          <ModalStyle.ContentTitle>Snooze until</ModalStyle.ContentTitle>
          <GapY $gap={'8px'} />
          <When condition={loading}>
            <ModalStyle.Text>{'Loading...'}</ModalStyle.Text>
          </When>
          <When condition={!loading}>
            <ModalStyle.Text>{date}</ModalStyle.Text>
          </When>
        </When>
      </ModalStyle.Content>
      <ModalStyle.Footer>
        <TripleX.Container>
          <TripleX.Rest></TripleX.Rest>
          <TripleX.Side>
            <StackX>
              <ModalStyle.FooterButton text={'AI Date Recognition'} onClick={handleTextToDate} disabled={loading}></ModalStyle.FooterButton>
              <When condition={stage === 2}>
                <ModalStyle.FooterButton
                  text={'Ok'}
                  onClick={() => {
                    const newEffect = ImmerFuncs.produce(effect, (draft) => {
                      draft.data.date = date;
                    });
                    onOk(newEffect);
                  }}
                ></ModalStyle.FooterButton>
              </When>
            </StackX>
          </TripleX.Side>
        </TripleX.Container>
      </ModalStyle.Footer>
    </ModalStyle.Container>
  );
}
